import { buildVehicleFilterUrl, loadVehicle } from "@theme/kenteken-api";
import { cloneSlideForLoop, setupProductSliderDrag, snapScrollToStep } from "@theme/vp-product-slider-drag";
//#region assets/vp-related-products.ts
/**
* Related products slider — infinite carousel chrome for PDP suggestions.
* When a vehicle is saved (kenteken / model), slides are loaded from the
* same brand_and_model_v2 collection filter as model-product-slider — but offset
* / deduped so this section shows different products than "Shop voor uw model".
*/
var PRODUCTS_SECTION_ID = "model-product-slider-products";
/**
* @param {import('@theme/kenteken-api').VpVehicle|null|undefined} vehicle
* @returns {boolean}
*/
function hasVehicleFilters(vehicle) {
	if (!vehicle) return false;
	return Boolean(vehicle.brandAndModel || vehicle.brandAndModels?.length);
}
/**
* @param {HTMLElement} slide
* @returns {string}
*/
function productIdFromSlide(slide) {
	return slide.querySelector("[data-product-id]")?.getAttribute("data-product-id") || "";
}
/**
* How many products the on-page model slider will take from the filtered pool.
* @returns {number}
*/
function modelSliderTakeCount() {
	const slider = document.querySelector("model-product-slider");
	const fromData = Number.parseInt(slider?.dataset.productsToShow || "", 10);
	if (fromData > 0) return fromData;
	return 6;
}
/**
* Product IDs currently shown in the model-product-slider (real slides only).
* @returns {Set<string>}
*/
function modelSliderProductIds() {
	/** @type {Set<string>} */
	const ids = /* @__PURE__ */ new Set();
	document.querySelectorAll("model-product-slider .model-product-slider__slide:not([data-clone]) [data-product-id]").forEach((el) => {
		const id = el.getAttribute("data-product-id");
		if (id) ids.add(id);
	});
	return ids;
}
var VpRelatedProductsSlider = class extends HTMLElement {
	/** @type {WeakMap<HTMLElement, number>} */
	#setWidths = /* @__PURE__ */ new WeakMap();
	/** @type {WeakMap<HTMLElement, number>} */
	offsets = /* @__PURE__ */ new WeakMap();
	/** @type {AbortController|null} */
	#productsAbort = null;
	/** @type {MutationObserver|null} */
	#trackObserver = null;
	/** @type {string|null} */
	defaultTrackHtml = null;
	/** @type {boolean} */
	#syncing = false;
	/** @type {(() => void)|null} */
	#teardownDrag = null;
	connectedCallback() {
		this.prevButton = this.querySelector("[data-prev]");
		this.nextButton = this.querySelector("[data-next]");
		this.clip = this.querySelector(".vp-related-products__clip");
		this.track = this.querySelector("[data-track]");
		this.collectionUrl = this.dataset.collectionUrl || "/collections/all";
		this.productsSectionId = this.dataset.productsSectionId || PRODUCTS_SECTION_ID;
		this.productsToShow = Number.parseInt(this.dataset.productsToShow || "6", 10) || 6;
		this.sourceProductId = this.dataset.productId || "";
		if (this.track instanceof HTMLElement) {
			this.captureDefaultTrack();
			this.#trackObserver = new MutationObserver(() => {
				if (this.#syncing) return;
				if (hasVehicleFilters(loadVehicle())) {
					this.syncVehicleProducts();
					return;
				}
				if (!this.track.querySelectorAll(".vp-related-products__slide:not([data-clone])").length) return;
				this.captureDefaultTrack();
				this.dedupeAgainstModelSlider();
				this.prepareTrack(this.track);
				this.applyStartOffset(this.track);
				this.updateNavVisibility();
				this.updateSectionVisibility(this.track.querySelectorAll(".vp-related-products__slide:not([data-clone])").length > 0);
			});
			this.#trackObserver.observe(this.track, { childList: true });
		}
		if (this.prevButton) this.prevButton.addEventListener("click", () => this.scrollByDirection(-1));
		if (this.nextButton) this.nextButton.addEventListener("click", () => this.scrollByDirection(1));
		if (this.track instanceof HTMLElement) {
			this.track.addEventListener("transitionend", (event) => {
				if (event.target !== this.track || event.propertyName !== "transform") return;
				this.normalizeLoopOffset(this.track);
			});
			this.prepareTrack(this.track);
			this.applyStartOffset(this.track);
		}
		if (this.clip instanceof HTMLElement) this.#teardownDrag = setupProductSliderDrag({
			clip: this.clip,
			getTrack: () => this.track instanceof HTMLElement ? this.track : null,
			usesTransform: () => this.usesTransform,
			getOffset: (track) => this.getOffset(track),
			setOffset: (track, offset, options) => this.setOffset(track, offset, options),
			getMaxOffset: (track) => this.getMaxOffset(track),
			getStep: (track) => this.getStep(track),
			snapTransformOffset: (track) => this.snapOffset(track),
			snapScrollOffset: (track) => this.snapScrollOffset(track),
			trackCanScroll: (track) => this.trackCanScroll(track),
			navSelector: ".vp-related-products__nav",
			onDragMove: () => {
				if (!this.usesTransform) this.wrapMobileScroll();
			},
			onDragEnd: () => {
				this.updateNavVisibility();
			}
		});
		this.syncVehicleProducts();
		window.addEventListener("vp-vehicle:change", this.onVehicleChange);
		this.clip?.addEventListener("scroll", this.onClipScroll, { passive: true });
		window.addEventListener("resize", this.onResize, { passive: true });
		this.updateNavVisibility();
	}
	disconnectedCallback() {
		this.#productsAbort?.abort();
		this.#trackObserver?.disconnect();
		this.#teardownDrag?.();
		this.#teardownDrag = null;
		this.clip?.removeEventListener("scroll", this.onClipScroll);
		window.removeEventListener("vp-vehicle:change", this.onVehicleChange);
		window.removeEventListener("resize", this.onResize);
	}
	onVehicleChange = () => {
		this.syncVehicleProducts();
	};
	captureDefaultTrack() {
		if (!(this.track instanceof HTMLElement) || hasVehicleFilters(loadVehicle())) return;
		const slides = [...this.track.querySelectorAll(".vp-related-products__slide:not([data-clone])")].filter((slide) => !slide.querySelector("[data-placeholder=\"true\"]"));
		if (!slides.length) return;
		this.defaultTrackHtml = slides.map((slide) => slide.outerHTML).join("");
	}
	restoreDefaultTrack() {
		if (!(this.track instanceof HTMLElement) || this.defaultTrackHtml == null) return;
		this.#syncing = true;
		this.track.innerHTML = this.defaultTrackHtml;
		this.#syncing = false;
		this.track.dataset.loop = "false";
		delete this.track.dataset.originalCount;
		this.#setWidths.delete(this.track);
		this.offsets.delete(this.track);
		this.track.style.transform = "";
		this.track.classList.remove("is-jumping");
		if (this.clip) this.clip.scrollLeft = 0;
		this.dedupeAgainstModelSlider();
		this.prepareTrack(this.track);
		this.applyStartOffset(this.track);
		if (this.clip && !this.usesTransform && !this.trackCanScroll(this.track)) this.clip.scrollLeft = 0;
		this.updateNavVisibility();
		this.updateSectionVisibility(this.track.querySelectorAll(".vp-related-products__slide:not([data-clone])").length > 0);
	}
	/**
	* Drop recommendation slides that are already in the model-product-slider.
	*/
	dedupeAgainstModelSlider() {
		if (!(this.track instanceof HTMLElement)) return;
		const excluded = modelSliderProductIds();
		if (this.sourceProductId) excluded.add(String(this.sourceProductId));
		if (!excluded.size) return;
		const realSlides = [...this.track.querySelectorAll(".vp-related-products__slide:not([data-clone])")];
		let removed = false;
		for (const slide of realSlides) {
			const id = productIdFromSlide(slide);
			if (id && excluded.has(id)) {
				slide.remove();
				removed = true;
			}
		}
		if (removed) {
			this.track.querySelectorAll("[data-clone=\"true\"]").forEach((clone) => clone.remove());
			this.track.dataset.loop = "false";
			delete this.track.dataset.originalCount;
			this.#setWidths.delete(this.track);
			this.offsets.delete(this.track);
			this.track.style.transform = "";
			if (this.clip) this.clip.scrollLeft = 0;
		}
	}
	/**
	* @param {boolean} hasProducts
	*/
	updateSectionVisibility(hasProducts) {
		const wrapper = this.closest("product-recommendations")?.querySelector("[data-has-recommendations]");
		if (wrapper instanceof HTMLElement) wrapper.dataset.hasRecommendations = hasProducts ? "true" : "false";
	}
	/**
	* @param {HTMLElement} slide
	* @returns {HTMLElement}
	*/
	#toRelatedSlide(slide) {
		const clone = slide.cloneNode(true);
		clone.classList.remove("model-product-slider__slide");
		clone.classList.add("vp-related-products__slide");
		clone.setAttribute("role", "listitem");
		clone.removeAttribute("data-clone");
		clone.removeAttribute("aria-hidden");
		return clone;
	}
	/**
	* Pick suggestion slides that the model slider will not show.
	* Uses the same filtered collection order: model slider takes the first N,
	* this section takes the next batch (then drops any leftover ID overlaps).
	*
	* @param {HTMLElement[]} slides
	* @returns {HTMLElement[]}
	*/
	#pickDistinctSlides(slides) {
		const excluded = modelSliderProductIds();
		if (this.sourceProductId) excluded.add(String(this.sourceProductId));
		const offset = modelSliderTakeCount();
		/** @type {HTMLElement[]} */
		const picked = [];
		/** @type {Set<string>} */
		const seen = /* @__PURE__ */ new Set();
		const consider = (slide) => {
			const id = productIdFromSlide(slide);
			if (id && (excluded.has(id) || seen.has(id))) return;
			if (id && this.sourceProductId && id === String(this.sourceProductId)) return;
			if (id) seen.add(id);
			picked.push(this.#toRelatedSlide(slide));
		};
		for (const slide of slides.slice(offset)) {
			consider(slide);
			if (picked.length >= this.productsToShow) break;
		}
		return picked;
	}
	async syncVehicleProducts() {
		if (!(this.track instanceof HTMLElement)) return;
		this.#productsAbort?.abort();
		const abortController = new AbortController();
		this.#productsAbort = abortController;
		const vehicle = loadVehicle();
		if (!hasVehicleFilters(vehicle)) {
			this.restoreDefaultTrack();
			return;
		}
		try {
			let slides = await this.#loadFilteredSlides(vehicle, abortController.signal);
			if (!slides.length && !abortController.signal.aborted) slides = await this.#loadFilteredSlides(vehicle, abortController.signal, { merkOnly: true });
			if (abortController.signal.aborted) return;
			this.#syncing = true;
			this.track.innerHTML = "";
			slides.forEach((slide) => this.track.append(slide));
			this.#syncing = false;
			this.prepareTrack(this.track);
			this.offsets.delete(this.track);
			this.track.style.transform = "";
			if (this.clip) this.clip.scrollLeft = 0;
			this.applyStartOffset(this.track);
			this.updateNavVisibility();
			this.updateSectionVisibility(slides.length > 0);
		} catch (error) {
			if (error?.name === "AbortError") return;
		}
	}
	/**
	* @param {import('@theme/kenteken-api').VpVehicle} vehicle
	* @param {AbortSignal} signal
	* @param {{ merkOnly?: boolean }} [options]
	* @returns {Promise<HTMLElement[]>}
	*/
	async #loadFilteredSlides(vehicle, signal, options = {}) {
		const filterUrl = buildVehicleFilterUrl(vehicle, this.collectionUrl, options);
		const url = new URL(filterUrl, window.location.origin);
		url.searchParams.set("section_id", this.productsSectionId);
		const response = await fetch(url.toString(), {
			method: "GET",
			headers: { Accept: "text/html" },
			signal
		});
		if (!response.ok) throw new Error("Unable to load vehicle-filtered suggestions");
		const html = await response.text();
		if (signal.aborted) return [];
		const nextTrack = new DOMParser().parseFromString(html, "text/html").querySelector("[data-track]");
		if (!(nextTrack instanceof HTMLElement)) return [];
		const sourceSlides = [...nextTrack.querySelectorAll(".model-product-slider__slide")];
		const slides = this.#pickDistinctSlides(sourceSlides);
		if (slides.length >= this.productsToShow || signal.aborted) return slides;
		const pageUrl = new URL(url.toString());
		pageUrl.searchParams.set("page", "2");
		const pageResponse = await fetch(pageUrl.toString(), {
			method: "GET",
			headers: { Accept: "text/html" },
			signal
		});
		if (!pageResponse.ok) return slides;
		const pageHtml = await pageResponse.text();
		if (signal.aborted) return slides;
		const pageTrack = new DOMParser().parseFromString(pageHtml, "text/html").querySelector("[data-track]");
		if (!(pageTrack instanceof HTMLElement)) return slides;
		const seen = new Set(slides.map((slide) => productIdFromSlide(slide)).filter(Boolean));
		const excluded = modelSliderProductIds();
		if (this.sourceProductId) excluded.add(String(this.sourceProductId));
		for (const slide of pageTrack.querySelectorAll(".model-product-slider__slide")) {
			const id = productIdFromSlide(slide);
			if (id && (excluded.has(id) || seen.has(id))) continue;
			if (id) seen.add(id);
			slides.push(this.#toRelatedSlide(slide));
			if (slides.length >= this.productsToShow) break;
		}
		return slides;
	}
	onResize = () => {
		const track = this.track;
		if (!(track instanceof HTMLElement)) return;
		const wasLooping = track.dataset.loop === "true";
		const count = Number.parseInt(track.dataset.originalCount || "0", 10);
		const realSlides = [...track.querySelectorAll(".vp-related-products__slide")].slice(0, count || void 0);
		const last = realSlides[realSlides.length - 1];
		if (wasLooping !== (Boolean(this.clip) && realSlides.length >= 2 && last != null && last.offsetLeft + last.offsetWidth > (this.clip?.clientWidth || 0) + 4)) {
			const offset = this.getOffset(track);
			this.prepareTrack(track);
			if (offset) this.setOffset(track, offset, { animate: false });
		} else this.measureSetWidth(track);
		this.clampActiveOffset();
		this.updateNavVisibility();
	};
	onClipScroll = () => {
		this.wrapMobileScroll();
		this.updateNavVisibility();
	};
	wrapMobileScroll() {
		if (this.usesTransform || !this.clip) return;
		const track = this.track;
		if (!(track instanceof HTMLElement) || track.dataset.loop !== "true") return;
		const setWidth = this.getSetWidth(track);
		if (setWidth <= 0) return;
		const { scrollLeft } = this.clip;
		if (scrollLeft >= setWidth - 1) this.clip.scrollLeft = scrollLeft - setWidth;
	}
	/**
	* @param {HTMLElement} track
	*/
	prepareTrack(track) {
		const trackObserver = this.#trackObserver;
		trackObserver?.disconnect();
		this.#syncing = true;
		try {
			track.querySelectorAll("[data-clone=\"true\"]").forEach((clone) => clone.remove());
			track.classList.remove("is-jumping");
			this.#setWidths.delete(track);
			const slides = [...track.querySelectorAll(".vp-related-products__slide:not([data-clone])")];
			track.dataset.originalCount = String(slides.length);
			track.dataset.loop = "false";
			if (slides.length < 2 || !this.clip) return;
			track.offsetWidth;
			const last = slides[slides.length - 1];
			if (last.offsetLeft + last.offsetWidth <= this.clip.clientWidth + 4) return;
			slides.forEach((slide) => track.append(cloneSlideForLoop(slide)));
			track.dataset.loop = "true";
			this.measureSetWidth(track);
		} finally {
			this.#syncing = false;
			trackObserver?.observe(track, { childList: true });
		}
	}
	/**
	* @param {HTMLElement} track
	*/
	measureSetWidth(track) {
		const count = Number.parseInt(track.dataset.originalCount || "0", 10);
		if (!count) {
			this.#setWidths.delete(track);
			return;
		}
		const slides = [...track.querySelectorAll(".vp-related-products__slide")];
		if (slides.length < count * 2) {
			this.#setWidths.delete(track);
			return;
		}
		const first = slides[0];
		const setWidth = slides[count].offsetLeft - first.offsetLeft;
		if (setWidth > 0) this.#setWidths.set(track, setWidth);
	}
	/**
	* @param {HTMLElement} track
	* @returns {number}
	*/
	getSetWidth(track) {
		if (!this.#setWidths.has(track)) this.measureSetWidth(track);
		return this.#setWidths.get(track) || 0;
	}
	get usesTransform() {
		return window.matchMedia("(min-width: 750px)").matches;
	}
	/**
	* Start on product 2 so the rail bleeds full-width (peek left + right).
	* @param {HTMLElement} track
	*/
	applyStartOffset(track) {
		if (!(track instanceof HTMLElement) || !this.clip) return;
		if (!this.trackCanScroll(track)) return;
		const step = this.getStep(track);
		if (!step) return;
		if (this.usesTransform) this.setOffset(track, step, { animate: false });
		else this.clip.scrollLeft = step;
	}
	/**
	* Snap transform offset to the nearest card step after a drag.
	* @param {HTMLElement} track
	*/
	snapOffset(track) {
		const step = this.getStep(track);
		if (!step) return;
		let offset = this.getOffset(track);
		const looping = track.dataset.loop === "true";
		const setWidth = this.getSetWidth(track);
		if (looping && setWidth > 0) {
			if (offset < 0) {
				this.setOffset(track, offset + setWidth, { animate: false });
				offset = this.getOffset(track);
			} else if (offset >= setWidth) {
				this.setOffset(track, offset - setWidth, { animate: false });
				offset = this.getOffset(track);
			}
		}
		offset = Math.round(offset / step) * step;
		if (!looping) offset = Math.max(0, Math.min(offset, this.getMaxOffset(track)));
		this.setOffset(track, offset, { animate: true });
	}
	/**
	* Snap native scroll position after a mobile drag.
	* @param {HTMLElement} track
	*/
	snapScrollOffset(track) {
		if (!this.clip) return;
		snapScrollToStep(this.clip, track, (activeTrack) => this.getStep(activeTrack));
	}
	/**
	* @param {HTMLElement} track
	*/
	getOffset(track) {
		return this.offsets.get(track) || 0;
	}
	/**
	* @param {HTMLElement} track
	* @param {number} offset
	* @param {{ animate?: boolean }} [options]
	*/
	setOffset(track, offset, { animate = true } = {}) {
		this.offsets.set(track, offset);
		if (!animate) track.classList.add("is-jumping");
		track.style.transform = offset ? `translate3d(-${offset}px, 0, 0)` : "";
		if (!animate) {
			track.offsetWidth;
			track.classList.remove("is-jumping");
		}
	}
	/**
	* @param {HTMLElement} track
	*/
	getMaxOffset(track) {
		if (!track || !this.clip) return 0;
		if (track.dataset.loop === "true") {
			const setWidth = this.getSetWidth(track);
			return setWidth > 0 ? setWidth : Math.max(0, track.scrollWidth - this.clip.clientWidth);
		}
		return Math.max(0, track.scrollWidth - this.clip.clientWidth);
	}
	/**
	* @param {HTMLElement} track
	*/
	getStep(track) {
		const slide = track.querySelector(".vp-related-products__slide");
		if (!slide) return 0;
		const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
		return slide.getBoundingClientRect().width + gap;
	}
	/**
	* @param {HTMLElement} track
	*/
	normalizeLoopOffset(track) {
		if (track.dataset.loop !== "true" || !this.usesTransform) return;
		const setWidth = this.getSetWidth(track);
		if (setWidth <= 0) return;
		let offset = this.getOffset(track);
		if (offset < 0 || offset >= setWidth) {
			offset = (offset % setWidth + setWidth) % setWidth;
			this.setOffset(track, offset, { animate: false });
		}
	}
	clampActiveOffset() {
		const track = this.track;
		if (!(track instanceof HTMLElement) || !this.usesTransform) return;
		if (track.dataset.loop === "true") {
			this.normalizeLoopOffset(track);
			return;
		}
		const maxOffset = this.getMaxOffset(track);
		const nextOffset = Math.min(this.getOffset(track), maxOffset);
		this.setOffset(track, nextOffset, { animate: false });
	}
	/**
	* @param {-1|1} direction
	*/
	scrollByDirection(direction) {
		const track = this.track;
		if (!(track instanceof HTMLElement)) return;
		const step = this.getStep(track);
		if (!step) return;
		if (!this.usesTransform) {
			const scroller = this.clip || track;
			if (scroller.scrollWidth - scroller.clientWidth <= 4) return;
			let nextLeft = scroller.scrollLeft + direction * step;
			if (track.dataset.loop === "true") {
				const setWidth = this.getSetWidth(track);
				if (setWidth > 0) {
					if (direction > 0 && nextLeft >= setWidth - 4) {
						scroller.scrollLeft = nextLeft - setWidth;
						nextLeft = scroller.scrollLeft + step;
					} else if (direction < 0 && nextLeft < 4) {
						scroller.scrollLeft = nextLeft + setWidth;
						nextLeft = scroller.scrollLeft - step;
					}
				}
			}
			scroller.scrollTo({
				left: nextLeft,
				behavior: "smooth"
			});
			return;
		}
		let offset = this.getOffset(track);
		const setWidth = this.getSetWidth(track);
		if (track.dataset.loop === "true" && setWidth > 0 && direction < 0 && offset < 4) {
			this.setOffset(track, offset + setWidth, { animate: false });
			offset = this.getOffset(track);
			requestAnimationFrame(() => {
				this.setOffset(track, offset + direction * step);
				this.updateNavVisibility();
			});
			return;
		}
		this.setOffset(track, offset + direction * step);
		this.updateNavVisibility();
	}
	updateNavVisibility() {
		const track = this.track;
		if (!(track instanceof HTMLElement)) return;
		const canScroll = this.trackCanScroll(track);
		if (this.prevButton) this.prevButton.hidden = !canScroll;
		if (this.nextButton) this.nextButton.hidden = !canScroll;
	}
	/**
	* @param {HTMLElement} track
	* @returns {boolean}
	*/
	trackCanScroll(track) {
		if (!this.clip) return false;
		if (track.dataset.loop === "true") return this.getSetWidth(track) > 4;
		if (this.usesTransform) return this.getMaxOffset(track) > 4;
		return this.clip.scrollWidth - this.clip.clientWidth > 4;
	}
};
if (!customElements.get("vp-related-products-slider")) customElements.define("vp-related-products-slider", VpRelatedProductsSlider);
//#endregion
